import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpDown, Check, GraduationCap, Palette, Plus, Pointer, ShieldCheck, Trophy, X } from "lucide-react";
import { PlayingCard } from "../PlayingCard";
import { PLAYER_COLORS } from "../../lib/playerColors";
import { t, textDir, useLang } from "../../lib/i18n";
import { dealerAvatar } from "../../lib/brand";

// The pictures of the tutorial (TutorialDialog), one per step. Every card is a
// real <PlayingCard>, so the lesson looks like the table the player will meet.
// Card rows read left to right in every language, as on the game board.

const card = (rank, suit) => ({ id: `${rank}${suit}`, rank, suit });
const JOKER = { id: "joker", rank: "JOKER", suit: null };
const SUIT = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };

// A small card, for the table of the turn picture and the hands of the scores.
function MiniCard({ c }) {
  const joker = c.rank === "JOKER";
  const red = c.suit === "hearts" || c.suit === "diamonds";
  return (
    <span
      className={`flex h-[30px] w-[22px] flex-col items-center justify-center rounded-[4px] bg-white text-[10px] font-bold leading-none shadow ${
        joker ? "text-fuchsia-700" : red ? "text-red-600" : "text-slate-900"
      }`}
    >
      {joker ? "★" : <>{c.rank}<span className="mt-px">{SUIT[c.suit]}</span></>}
    </span>
  );
}

// Steps through the phases of a picture (`durations[i]` ms each, then again)
// and counts the loops. Stays on phase `still` when the system asks for less
// motion.
function usePhase(durations, still) {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const id = setTimeout(() => setTick((n) => n + 1), durations[tick % durations.length]);
    return () => clearTimeout(id);
  }, [tick, reduce, durations]);
  if (reduce) return [still, 0];
  return [tick % durations.length, Math.floor(tick / durations.length)];
}

const SPRING = { type: "spring", stiffness: 190, damping: 21 };

// ── 1. The goal: the hand flies away, the trophy comes ──────────────────────
const GOAL_HAND = [card("K", "spades"), card("Q", "hearts"), JOKER, card("7", "diamonds"), card("A", "clubs")];
const GOAL_TIMES = [1800, 2300];

export function GoalVisual() {
  const [phase] = usePhase(GOAL_TIMES, 1);
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <AnimatePresence>
        {phase === 0 && (
          <motion.div key="hand" dir="ltr" className="flex items-end pt-3">
            {GOAL_HAND.map((c, i) => {
              const k = i - 2;
              return (
                <motion.div
                  key={c.id}
                  className="-mx-2 origin-bottom"
                  initial={{ opacity: 0, y: 40, rotate: 0 }}
                  animate={{ opacity: 1, y: Math.abs(k) * 6, rotate: k * 10, transition: { ...SPRING, delay: i * 0.07 } }}
                  exit={{ opacity: 0, y: -90, rotate: k * 10 + 20, transition: { delay: i * 0.09, duration: 0.4, ease: "easeIn" } }}
                >
                  <PlayingCard card={c} size="sm" />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {phase === 1 && (
          <motion.div
            key="win"
            className="absolute flex flex-col items-center gap-2"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1, transition: { type: "spring", stiffness: 280, damping: 15, delay: 0.55 } }}
            exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.2 } }}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 ring-1 ring-amber-300/50 rami-glow-gold">
              <Trophy className="h-7 w-7 text-amber-300" />
            </span>
            <span className="font-display text-lg text-amber-200">{t("tuto.goal.win")}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── 2. A turn, on a miniature of the game screen ────────────────────────────
// The bottom of the real board, shrunk to the panel (the opponents' row left
// out): the table, the draw / discard column on the side the player chose,
// the dealer's hint over the player's bar, and the hand running off the edge.
// A finger taps the draw pile: the card flies into the hand (15). Then it
// drags that card onto the discard pile, which lights up as in the game (14).
// In the game the draw pile is tapped, never dragged. Like the board, the
// picture never mirrors; its words are the game's own (pioche.*, game.hint.*).
const TURN_TIMES = [1000, 450, 1000, 900, 600, 1000, 1600];
//                  aim   tap  fly   wait  grab  drag  drop
const BOARD_W = 300;
const CARD_W = 32; // the xs card of the game
const SLICE = 15; // what shows of a card in the hand
const HAND = [
  card("A", "clubs"), card("2", "hearts"), card("3", "hearts"), card("4", "hearts"), card("6", "spades"),
  card("7", "diamonds"), card("7", "clubs"), card("9", "spades"), card("10", "hearts"), card("J", "clubs"),
  card("Q", "diamonds"), card("K", "spades"), card("K", "hearts"), JOKER,
];
const HAND_X = (BOARD_W - (CARD_W + HAND.length * SLICE)) / 2; // leaves room for a 15th card
const HAND_Y = 170;
const DRAWN = card("2", "clubs");
const TOP = card("9", "spades");
const COL_W = 52; // the draw / discard column
const TIP = { x: 9.3, y: 2 }; // the fingertip in the 28px pointer
const DRAG = { duration: 0.8, ease: "easeInOut" };
const TABLE = [
  { who: "tuto.who.sami", color: PLAYER_COLORS[1], set: false, pts: 18, cards: [card("5", "diamonds"), card("6", "diamonds"), card("7", "diamonds")] },
  { who: "tuto.who.leila", color: PLAYER_COLORS[2], set: true, pts: 30, cards: [card("K", "spades"), card("K", "hearts"), card("K", "clubs")] },
];
// The dealer's bubble, coloured as in GameFeed (HINT_STYLE).
const HINT_BUBBLE = {
  drawing: "bg-amber-950/70 border-amber-400/35 text-amber-100/90",
  acting: "bg-slate-900/70 border-white/12 text-white/60",
};

// First letter of a translated name (t() wraps Tunisian in bidi marks).
const initial = (s) => [...s.replace(/[‎⁦-⁩]/g, "")][0];

// Same side as on the player's own board (chosen on the home page).
function readPiocheLeft() {
  try {
    return localStorage.getItem("pioche_side") === "left";
  } catch {
    return false;
  }
}

// A pointing hand that reads on the felt and on a white card alike.
function Finger() {
  return (
    <span className="relative block h-7 w-7 drop-shadow-[0_3px_4px_rgba(0,0,0,0.55)]">
      <Pointer className="absolute inset-0 h-7 w-7 text-white" fill="white" strokeWidth={4.5} />
      <Pointer className="absolute inset-0 h-7 w-7 text-slate-900" strokeWidth={1.6} />
    </span>
  );
}

// A combination on the table, drawn like TableCombinations.
function MiniCombo({ left, who, color, set, pts, cards }) {
  const name = t(who);
  return (
    <div
      className="absolute overflow-hidden rounded-lg border border-white/10 bg-gradient-to-b from-slate-900/70 to-slate-950/80"
      style={{ left, top: 6 }}
    >
      <div className="flex items-center gap-1 border-b border-white/[0.06] px-1.5 pb-0.5 pt-1">
        <span
          className="flex h-2.5 w-2.5 items-center justify-center rounded-full text-[5px] font-bold"
          style={{ background: color.bg, color: color.hex, boxShadow: `0 0 0 1px ${color.border}` }}
        >
          {initial(name)}
        </span>
        <span className="text-[6.5px] font-semibold leading-none" style={{ color: color.hex }}>{name}</span>
        <span
          lang="fr"
          className={`rounded px-0.5 text-[5.5px] font-bold uppercase ${set ? "bg-violet-500/20 text-violet-300" : "bg-sky-500/20 text-sky-300"}`}
        >
          {set ? t("table.set") : t("table.seq")}
        </span>
        <span className="ms-auto ps-1 text-[6.5px] font-bold tabular-nums text-amber-300">{pts}</span>
      </div>
      <div className="flex gap-0.5 p-1">
        {cards.map((c) => <MiniCard key={c.id} c={c} />)}
      </div>
    </div>
  );
}

// "PRENDRE" across the bottom of a card, as in PiocheArea.
const TakeBadge = () => (
  <div className="absolute inset-x-0 bottom-0 flex items-center justify-center rounded-b-md border-t border-amber-400/40 bg-black/60 py-px">
    <span className="text-[5.5px] font-bold uppercase text-amber-300">{t("pioche.take")}</span>
  </div>
);

export function TurnVisual() {
  const { lang } = useLang();
  const [phase] = usePhase(TURN_TIMES, 5);
  const [piocheLeft] = useState(readPiocheLeft);
  const drawing = phase <= 1; // my turn, nothing drawn yet
  const acting = phase >= 2 && phase <= 5; // drawn, the discard is due
  const count = acting ? 15 : 14;

  const colX = piocheLeft ? 4 : BOARD_W - 4 - COL_W;
  const cx = colX + COL_W / 2;
  const PILE = { x: cx - CARD_W / 2, y: 24 };
  const ON_TOP = { x: cx - CARD_W / 2, y: 95 }; // the top of the discard pile
  const SLOT = { x: HAND_X + HAND.length * SLICE, y: HAND_Y }; // the 15th card of the hand
  const LIFTED = { x: SLOT.x, y: HAND_Y - 14 };
  const touch = (at) => ({ x: at.x + CARD_W / 2 - TIP.x, y: at.y + 24 - TIP.y });
  const rest = { x: BOARD_W / 2 - TIP.x, y: 120 };
  const tableX = piocheLeft ? 64 : 8;
  // The drawn card arcs into the hand, around the discard pile that sits under the draw pile.
  const ARC = piocheLeft ? { x: (PILE.x + SLOT.x) / 2, y: 70 } : { x: PILE.x - 70, y: 100 };
  const fly = { duration: 0.75, ease: "easeInOut", times: [0, 0.5, 1] };

  // Every phase sets every value: one left out would spring back to its first
  // value (the tilt of the dropped card).
  const drawn = [
    { ...ON_TOP, opacity: 0, scale: 1, rotate: 0, transition: { duration: 0.3 } }, // last loop's card leaves
    { ...PILE, opacity: 0, scale: 1, rotate: 0, transition: { duration: 0 } },
    {
      x: [PILE.x, ARC.x, SLOT.x],
      y: [PILE.y, ARC.y, SLOT.y],
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { x: fly, y: fly, default: { duration: 0 } },
    },
    { ...SLOT, opacity: 1, scale: 1, rotate: 0 },
    { ...LIFTED, opacity: 1, scale: 1.1, rotate: -4, transition: { duration: 0.2 } },
    { ...ON_TOP, opacity: 1, scale: 1.1, rotate: -4, transition: DRAG },
    { ...ON_TOP, opacity: 1, scale: 1, rotate: 0, transition: { type: "spring", stiffness: 300, damping: 18 } },
  ][phase];
  // Face down while it leaves the pile, face up once in the hand.
  const flip = phase === 2 ? { duration: 0.75, times: [0, 0.45, 0.6] } : { duration: 0 };

  const finger = [
    { x: [rest.x, touch(PILE).x], y: [rest.y, touch(PILE).y], opacity: [0, 1], scale: 1, transition: { duration: 0.7, ease: "easeOut" } },
    { ...touch(PILE), opacity: 1, scale: [1, 0.8, 1], transition: { duration: 0.35 } }, // tap
    { x: touch(PILE).x, y: touch(PILE).y + 24, opacity: 0, scale: 1, transition: { duration: 0.4 } },
    { x: touch(LIFTED).x, y: touch(LIFTED).y + 24, opacity: 0, scale: 1, transition: { duration: 0 } },
    { ...touch(LIFTED), opacity: 1, scale: 0.85, transition: { duration: 0.3 } }, // holds the card
    { ...touch(ON_TOP), opacity: 1, scale: 0.85, transition: DRAG }, // drags it
    { x: touch(ON_TOP).x, y: touch(ON_TOP).y + 16, opacity: 0, scale: 1, transition: { duration: 0.4, delay: 0.25 } },
  ][phase];

  const banner = drawing ? t("pioche.draw") : acting ? t("pioche.discard") : null;
  const hint = drawing ? { type: "drawing", text: t("game.hint.draw") } : acting ? { type: "acting", text: t("game.hint.act") } : null;
  // The discard pile: waiting for a card while one is due, lit up while one is dragged over it.
  const zone =
    phase === 5
      ? "border-amber-400/80 bg-amber-500/[0.18] shadow-[0_0_14px_rgba(251,191,36,0.32)] delay-300"
      : acting
        ? "border-amber-400/30 bg-amber-500/[0.04]"
        : "border-transparent";

  return (
    <div dir="ltr" className="relative h-[206px] w-[300px]">
      {/* The table */}
      {TABLE.map((combo, i) => (
        <MiniCombo key={combo.who} left={tableX + i * 84} {...combo} />
      ))}

      {/* The draw / discard column (PiocheArea) */}
      <div
        className={`absolute top-[3px] flex h-[10px] items-center justify-center gap-0.5 transition-opacity duration-300 ${banner ? "" : "opacity-0"}`}
        style={{ left: colX, width: COL_W }}
      >
        <span className="h-1 w-1 animate-pulse rounded-full bg-amber-400" />
        <span className="text-[6.5px] font-semibold uppercase text-amber-300">{banner}</span>
      </div>
      <span className="absolute top-[14px] text-center text-[5.5px] uppercase tracking-widest text-amber-200/60" style={{ left: colX, width: COL_W }}>
        {t("pioche.pileCount", { n: drawing ? 42 : 41 })}
      </span>
      <div className={`absolute transition-opacity duration-300 ${drawing ? "" : "opacity-40"}`} style={{ left: PILE.x, top: PILE.y }}>
        {drawing && (
          <motion.div
            className="absolute -inset-1 rounded-lg"
            animate={{ boxShadow: ["0 0 0 0px rgba(245,197,66,0.55)", "0 0 0 5px rgba(245,197,66,0)"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <div className={`relative rounded-lg ${drawing ? "rami-glow-gold" : ""}`}>
          <PlayingCard faceDown size="xs" />
          {drawing && <TakeBadge />}
        </div>
      </div>
      <div
        className={`absolute top-[75px] flex items-center gap-0.5 px-1 transition-opacity duration-300 ${drawing ? "" : "opacity-0"}`}
        style={{ left: colX, width: COL_W }}
      >
        <div className="h-px flex-1 bg-slate-600/45" />
        <span className="text-[5.5px] text-slate-400">{t("common.or")}</span>
        <div className="h-px flex-1 bg-slate-600/45" />
      </div>
      <div
        className={`absolute top-[84px] h-[62px] w-[42px] rounded-lg border-2 transition-all duration-300 ${zone}`}
        style={{ left: cx - 21 }}
      >
        <span
          className={`absolute inset-x-0 top-[1px] text-center text-[5.5px] uppercase ${
            acting ? "font-semibold text-amber-300/80" : "tracking-widest text-amber-200/60"
          }`}
        >
          {acting ? t("pioche.discardLabel") : t("pioche.fausseCount", { n: drawing ? 10 : 11 })}
        </span>
      </div>
      <div className="absolute" style={{ left: ON_TOP.x, top: ON_TOP.y }}>
        <div className={`relative rounded-lg ${drawing ? "rami-glow-gold" : "opacity-90"}`}>
          <PlayingCard card={TOP} size="xs" />
          {drawing && <TakeBadge />}
        </div>
      </div>

      {/* The dealer's hint over the player's bar, then the bar (BottomActions) */}
      <div className="absolute inset-x-0 top-[126px] flex justify-center">
        <div className="flex flex-col items-start gap-1">
          <div className="flex h-[19px] items-end gap-1">
            <img src={dealerAvatar(lang)} alt="" className="h-3.5 w-3.5 rounded-full object-cover ring-1 ring-amber-400/50" />
            <AnimatePresence mode="wait">
              {hint && (
                <motion.div
                  key={hint.type}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.88 }}
                  dir={textDir(hint.text)}
                  className={`max-w-[150px] rounded-xl rounded-bl-sm border px-1.5 py-0.5 text-[5.5px] font-semibold uppercase leading-snug ${HINT_BUBBLE[hint.type]}`}
                >
                  {hint.text}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className={`flex items-center gap-1 rounded-xl border bg-black/50 py-0.5 pe-2 ps-0.5 transition-colors duration-300 ${
                drawing || acting ? "border-amber-400/55" : "border-white/10"
              }`}
            >
              <div className="relative">
                <span
                  className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[6px] font-bold"
                  style={{ background: PLAYER_COLORS[0].bg, color: PLAYER_COLORS[0].hex, boxShadow: `0 0 0 1.5px ${PLAYER_COLORS[0].border}` }}
                >
                  {initial(t("common.me"))}
                </span>
                <motion.span
                  key={count}
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  className="absolute -right-2 -top-1.5 flex h-2.5 min-w-[11px] items-center justify-center rounded-full border border-white/15 bg-slate-900/95 px-0.5 text-[5px] font-bold tabular-nums text-white/75"
                >
                  {count}
                </motion.span>
              </div>
              <div className="flex flex-col gap-px">
                <span className="text-[6.5px] font-semibold leading-none text-white/90">{t("common.me")}</span>
                {drawing || acting ? (
                  <span className="flex items-center gap-0.5 text-[5px] font-semibold uppercase leading-none text-amber-300/95">
                    <span className="h-1 w-1 animate-pulse rounded-full bg-amber-400" />
                    {t("bottom.yourTurn")}
                  </span>
                ) : (
                  <span className="text-[5px] leading-none text-white/30">{t("bottom.nCards", { n: count })}</span>
                )}
              </div>
            </div>
            <div className="flex items-center rounded-xl border border-white/10 bg-black/40 p-px">
              <span className="flex items-center gap-0.5 rounded-[10px] bg-amber-400/15 px-1 py-0.5 text-[5.5px] font-medium text-amber-300">
                <ArrowUpDown className="h-2 w-2" />
                {t("bottom.sortValue")}
              </span>
              <span className="flex items-center gap-0.5 px-1 py-0.5 text-[5.5px] font-medium text-white/40">
                <Palette className="h-2 w-2" />
                {t("bottom.sortSuit")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* The hand (PlayerHand), running off the bottom of the screen */}
      {HAND.map((c, i) => (
        <div key={c.id} className="absolute" style={{ left: HAND_X + i * SLICE, top: HAND_Y }}>
          <PlayingCard card={c} size="xs" />
        </div>
      ))}

      {/* The tap on the draw pile */}
      <AnimatePresence>
        {phase === 1 && (
          <motion.span
            key="tap"
            className="absolute h-8 w-8 rounded-full border-2 border-white/80"
            style={{ left: PILE.x, top: PILE.y + 8 }}
            initial={{ scale: 0.3, opacity: 0.9 }}
            animate={{ scale: 1.6, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* The drawn card */}
      <motion.div className="absolute left-0 top-0" initial={false} animate={drawn}>
        <motion.div initial={false} animate={{ opacity: phase === 1 ? 0 : phase === 2 ? [0, 0, 1] : 1 }} transition={flip}>
          <PlayingCard card={DRAWN} size="xs" />
        </motion.div>
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: phase === 1 ? 1 : phase === 2 ? [1, 1, 0] : 0 }}
          transition={flip}
        >
          <PlayingCard faceDown size="xs" />
        </motion.div>
      </motion.div>

      {/* The finger, pressing from its tip */}
      <motion.div
        className="absolute left-0 top-0"
        style={{ originX: TIP.x / 28, originY: TIP.y / 28 }}
        initial={false}
        animate={finger}
      >
        <Finger />
      </motion.div>
    </div>
  );
}

// ── 3. The two combinations ─────────────────────────────────────────────────
const SEQ = [card("5", "hearts"), card("6", "hearts"), card("7", "hearts"), card("8", "hearts")];
const SET = [card("9", "spades"), card("9", "hearts"), card("9", "diamonds")];

function ComboRow({ name, tag, cards, delay }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 shrink-0 text-end leading-tight">
        <div className="text-xs font-semibold text-emerald-50">{name}</div>
        {/* The tag the table shows on this kind of combination (kept in French) */}
        <span lang="fr" className="mt-0.5 inline-block rounded bg-white/10 px-1 text-[9px] font-bold uppercase text-emerald-100/70">
          {tag}
        </span>
      </div>
      <div dir="ltr" className="flex gap-1">
        {cards.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: delay + i * 0.08 }}>
            <PlayingCard card={c} size="sm" />
          </motion.div>
        ))}
      </div>
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 15, delay: delay + cards.length * 0.08 + 0.15 }}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300"
      >
        <Check className="h-3 w-3" />
      </motion.span>
    </div>
  );
}

export function CombosVisual() {
  return (
    <div className="flex flex-col gap-3">
      <ComboRow name={t("tuto.combos.seq")} tag={t("table.seq")} cards={SEQ} delay={0.1} />
      <ComboRow name={t("tuto.combos.set")} tag={t("table.set")} cards={SET} delay={0.5} />
    </div>
  );
}

// ── 4. The Joker: the real card takes its place, the Joker goes to the hand ──
const JOKER_TIMES = [1500, 1600, 2100];
const J_SLOT = { x: 68, y: 84 }; // the Joker's place in 7♣ ★ 9♣
const J_HAND = { x: 180, y: 8 }; // a card in your hand

export function JokerVisual() {
  const [phase, loop] = usePhase(JOKER_TIMES, 2);
  const swapped = phase >= 1;
  return (
    <motion.div key={loop} dir="ltr" className="relative h-[160px] w-[236px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="absolute left-3 top-[74px] h-[84px] w-[156px] rounded-xl border border-white/10 bg-slate-950/35" />
      <div className="absolute left-5 top-[84px]"><PlayingCard card={card("7", "clubs")} size="sm" /></div>
      <div className="absolute left-[116px] top-[84px]"><PlayingCard card={card("9", "clubs")} size="sm" /></div>
      <span className="absolute left-[168px] top-[76px] w-[68px] text-center text-[10px] text-emerald-100/60">{t("tuto.hand")}</span>
      <motion.div className="absolute left-0 top-0" initial={false} animate={swapped ? J_HAND : J_SLOT} transition={SPRING}>
        <div className={`rounded-lg ${swapped ? "" : "ring-2 ring-fuchsia-400"}`}>
          <PlayingCard card={JOKER} size="sm" />
        </div>
        {!swapped && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 animate-pulse items-center justify-center rounded-full bg-fuchsia-500 text-[9px] text-white">★</span>
        )}
      </motion.div>
      <motion.div className="absolute left-0 top-0" initial={false} animate={swapped ? J_SLOT : J_HAND} transition={SPRING}>
        <div className={`rounded-lg ring-2 ${swapped ? "ring-emerald-400" : "ring-amber-300"}`}>
          <PlayingCard card={card("8", "clubs")} size="sm" />
        </div>
      </motion.div>
      <AnimatePresence>
        {phase === 2 && (
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute left-3 top-6 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-200"
          >
            ♻ {t("table.jokerRecovered")}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── 5. Why the first pose, and how the round is won ─────────────────────────
// The gestures of the game, on a small board. The column on the right says
// what the round would cost if it ended now: 100 while nothing is posed; once
// a run is down, only the cards left in the hand; nothing once the hand is
// empty. The finger taps three cards and drags them onto the table's bar (the
// first pose), drags the 9♥ onto that run, then the K♠, the last card, onto
// the discard pile: the hand is empty, the round is won (Nefrech).
const W_RUN = [card("6", "hearts"), card("7", "hearts"), card("8", "hearts")];
const W_ADD = card("9", "hearts");
const W_LAST = card("K", "spades");
const W_HAND = [...W_RUN, W_ADD, W_LAST];
const W_TOP = card("5", "clubs"); // already on the discard pile
const W_TIMES = [600, 300, 300, 300, 400, 800, 1100, 450, 800, 700, 450, 800, 2600];
//               aim  tap ×3        grab drag drop  9♥: grab drag drop  K♠: grab drag drop
const W_HAND_Y = 150;
const W_MID = 103; // the middle of the table, and of the hand under it
const W_TABLE = { x: 14, y: 24 }; // the first card of the run
const W_GAP = 35; // card + gap, on the table
const W_BAR = { x: W_MID - CARD_W / 2, y: 60 }; // the stack, over the bar
const W_PILE = { x: 237, y: 33 }; // the top of the discard pile
const W_COST = [
  { points: "+100", label: "tuto.save.none", color: "text-rose-300" },
  { points: "+19", label: "tuto.save.saved", color: "text-amber-200", Icon: ShieldCheck },
  { points: "+10", label: "tuto.save.saved", color: "text-amber-200", Icon: ShieldCheck },
  { points: "0", label: "tuto.save.won", color: "text-emerald-300", Icon: Trophy },
];
const wCost = (phase) => (phase < 6 ? 0 : phase < 9 ? 1 : phase < 12 ? 2 : 3);
const isRun = (c) => W_RUN.some((r) => r.id === c.id);
// A card keeps its slot in the hand until it lands somewhere.
const keepsSlot = (c, phase) => (isRun(c) ? phase < 6 : c.id === W_ADD.id ? phase < 9 : phase < 12);

export function SaveVisual() {
  useLang();
  const [phase, loop] = usePhase(W_TIMES, W_TIMES.length - 1);
  const cost = W_COST[wCost(phase)];
  const row = W_HAND.filter((c) => keepsSlot(c, phase));
  const handX = W_MID - (CARD_W + (row.length - 1) * SLICE) / 2;
  const slot = (c) => ({ x: handX + row.findIndex((r) => r.id === c.id) * SLICE, y: W_HAND_Y });
  const lifted = (c) => ({ x: slot(c).x, y: W_HAND_Y - 14 });
  const stackAt = phase === 4 ? { x: slot(W_RUN[2]).x, y: W_HAND_Y - 30 } : W_BAR;
  const runEnd = { x: W_TABLE.x + 3 * W_GAP, y: W_TABLE.y };
  const still = { opacity: 1, scale: 1, rotate: 0 };
  const held = { opacity: 1, scale: 1.1, rotate: -4 };

  // Where each card is, phase by phase (every phase sets every value).
  const placeOf = (c) => {
    if (isRun(c)) {
      const j = W_RUN.findIndex((r) => r.id === c.id);
      if (phase < 4) return { ...slot(c), ...still };
      if (phase < 6) {
        // As in the DragOverlay: the held card in front, the others behind it.
        const behind = 2 - j;
        return { x: stackAt.x + behind * 6, y: stackAt.y - behind * 3, opacity: behind > 0 ? 0.6 : 1, scale: 1, rotate: 0 };
      }
      return { x: W_TABLE.x + j * W_GAP, y: W_TABLE.y, ...still };
    }
    if (c.id === W_ADD.id) {
      if (phase < 7) return { ...slot(c), ...still };
      if (phase === 7) return { ...lifted(c), ...held };
      if (phase === 8) return { ...runEnd, ...held };
      return { ...runEnd, ...still };
    }
    if (phase < 10) return { ...slot(c), ...still };
    if (phase === 10) return { ...lifted(c), ...held };
    if (phase === 11) return { ...W_PILE, ...held };
    return { ...W_PILE, ...still };
  };
  const moving = (c) =>
    isRun(c) ? phase === 4 || phase === 5 : c.id === W_ADD.id ? phase === 7 || phase === 8 : phase === 10 || phase === 11;

  // The finger, pressing from its tip.
  const tapAt = (c) => ({ x: slot(c).x + 7 - TIP.x, y: W_HAND_Y + 16 - TIP.y });
  const onCard = (at) => ({ x: at.x + CARD_W / 2 - TIP.x, y: at.y + 24 - TIP.y });
  const reach = { x: { duration: 0.3 }, y: { duration: 0.3 }, scale: { duration: 0.35 } };
  const finger = [
    { ...tapAt(W_RUN[0]), x: [BOARD_W / 2, tapAt(W_RUN[0]).x], y: [120, tapAt(W_RUN[0]).y], opacity: [0, 1], scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
    ...W_RUN.map((c) => ({
      ...tapAt(c),
      opacity: 1,
      scale: [1, 1, 0.8, 1],
      transition: { x: { duration: 0.12 }, y: { duration: 0.12 }, scale: { duration: 0.4, times: [0, 0.45, 0.75, 1] } },
    })),
    { ...onCard(stackAt), opacity: 1, scale: 0.85, transition: { duration: 0.25 } }, // holds the three
    { ...onCard(W_BAR), opacity: 1, scale: 0.85, transition: DRAG },
    { ...onCard(W_BAR), opacity: 1, scale: 1, transition: { duration: 0.2 } }, // lets go
    { ...onCard(lifted(W_ADD)), opacity: 1, scale: 0.85, transition: reach },
    { ...onCard(runEnd), opacity: 1, scale: 0.85, transition: DRAG },
    { ...onCard(runEnd), opacity: 1, scale: 1, transition: { duration: 0.2 } },
    { ...onCard(lifted(W_LAST)), opacity: 1, scale: 0.85, transition: reach },
    { ...onCard(W_PILE), opacity: 1, scale: 0.85, transition: DRAG },
    { x: onCard(W_PILE).x, y: onCard(W_PILE).y + 16, opacity: 0, scale: 1, transition: { duration: 0.4, delay: 0.25 } },
  ][phase];

  return (
    <motion.div key={loop} dir="ltr" className="relative h-[206px] w-[300px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* The table: the run once posed, then the bar for a new group */}
      <div className="absolute left-0 top-0 h-[102px] w-[206px] rounded-xl border border-white/10 bg-slate-950/40" />
      {phase >= 6 && (
        <motion.div
          className={`absolute overflow-hidden rounded-lg border transition-colors duration-200 ${
            phase === 8 ? "border-emerald-400/70 bg-emerald-400/[0.08] shadow-[0_0_12px_rgba(52,211,153,0.25)] delay-300" : "border-white/10 bg-slate-900/50"
          }`}
          style={{ left: W_TABLE.x - 5, top: W_TABLE.y - 15, height: 68 }}
          initial={{ opacity: 0, width: 3 * W_GAP + 7 }}
          animate={{ opacity: 1, width: (phase >= 9 ? 4 : 3) * W_GAP + 7 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-1 px-1.5 pt-0.5">
            <span lang="fr" className="rounded bg-sky-500/20 px-0.5 text-[5.5px] font-bold uppercase text-sky-300">{t("table.seq")}</span>
            <span className="ms-auto text-[6.5px] font-bold tabular-nums text-amber-300">{phase >= 9 ? 30 : 21}</span>
          </div>
        </motion.div>
      )}
      <div
        className={`absolute flex h-[18px] items-center justify-center rounded-lg border border-dashed transition-all duration-200 ${
          phase === 5 ? "border-amber-300/70 bg-amber-400/[0.07] shadow-[0_0_10px_rgba(251,191,36,0.18)] delay-300" : "border-white/25"
        }`}
        style={{ left: 8, top: 78, width: 190 }}
      >
        <span className="text-[6px] font-bold uppercase tracking-[0.14em] text-white/60">{t("table.drop3")}</span>
      </div>

      {/* The discard pile (PiocheArea), lit up while the last card is dragged over it */}
      <span className="absolute top-[16px] w-[46px] text-center text-[6px] font-semibold uppercase text-amber-300/80" style={{ left: 230 }}>
        {t("pioche.discardLabel")}
      </span>
      <div
        className={`absolute top-[26px] h-[62px] w-[42px] rounded-lg border-2 transition-all duration-300 ${
          phase === 11 ? "border-amber-400/80 bg-amber-500/[0.18] shadow-[0_0_14px_rgba(251,191,36,0.32)] delay-300" : "border-amber-400/30 bg-amber-500/[0.04]"
        }`}
        style={{ left: 232 }}
      />
      <div className="absolute opacity-90" style={{ left: W_PILE.x, top: W_PILE.y, zIndex: 29 }}>
        <PlayingCard card={W_TOP} size="xs" />
      </div>

      {/* The hand: a picked card rises; the cards go where the finger takes them */}
      {W_HAND.map((c, i) => {
        const j = W_RUN.findIndex((r) => r.id === c.id);
        const behind = j >= 0 ? 2 - j : 0;
        const z = moving(c) ? 45 - behind : c.id === W_LAST.id && phase === 12 ? 30 : i;
        return (
          <motion.div
            key={c.id}
            className="absolute left-0 top-0"
            style={{ zIndex: z }}
            initial={false}
            animate={placeOf(c)}
            transition={phase === 5 || phase === 8 || phase === 11 ? DRAG : SPRING}
          >
            <PlayingCard card={c} size="xs" selected={j >= 0 && phase >= 1 && phase < 4 && j < phase} />
            {j === 2 && (phase === 4 || phase === 5) && (
              <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[7px] font-bold text-slate-950">
                3
              </span>
            )}
          </motion.div>
        );
      })}

      {/* The hand is empty: the round is won */}
      {phase === W_TIMES.length - 1 && (
        <motion.span
          className="rami-glow-gold absolute flex h-11 w-11 items-center justify-center rounded-full bg-amber-400/15 ring-1 ring-amber-300/50"
          style={{ left: W_MID - 22, top: W_HAND_Y - 2 }}
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 280, damping: 15, delay: 0.45 }}
        >
          <Trophy className="h-6 w-6 text-amber-300" />
        </motion.span>
      )}

      {/* What the round would cost if it ended now */}
      <div className="absolute bottom-3 right-0 flex w-[94px] flex-col items-center text-center">
        <span className="text-[9px] leading-tight text-emerald-100/60">{t("tuto.save.ifEnds")}</span>
        <motion.span
          key={cost.points}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 16 }}
          className={`mt-1 font-display text-3xl tabular-nums ${cost.color}`}
        >
          {cost.points}
        </motion.span>
        <span className={`mt-1 flex items-center justify-center gap-1 whitespace-nowrap text-[10px] font-semibold leading-tight ${cost.color}`}>
          {cost.Icon && <cost.Icon className="h-3 w-3 shrink-0" />}
          {t(cost.label)}
        </span>
      </div>

      <motion.div
        className="absolute left-0 top-0 z-50"
        style={{ originX: TIP.x / 28, originY: TIP.y / 28 }}
        initial={false}
        animate={finger}
      >
        <Finger />
      </motion.div>
    </motion.div>
  );
}

// ── 6. The first pose: select cards in the hand, drop them on the table ─────
// The same kind of miniature as the turn: the threshold badge of the top bar,
// the table with its "new group" bar, the discard pile, the hand running off
// the edge. The finger taps five cards, which rise as in PlayerHand, and drags
// them as one stack (the count badge of the game's DragOverlay) onto the bar:
// they become a group being composed, a run without a Joker (49). Then three
// 8s (24): the counter of the top bar passes the threshold (73 / 71). Last, it
// drags the K♠ onto the discard pile: that ends the turn and posts the groups,
// and the threshold goes up for the others (the pose + 1: 74).
const POSE_HAND = [
  card("2", "diamonds"), card("3", "clubs"), card("4", "clubs"), card("5", "diamonds"), card("6", "spades"),
  card("8", "spades"), card("8", "diamonds"), card("8", "clubs"), card("9", "hearts"), card("10", "hearts"),
  card("J", "hearts"), card("Q", "hearts"), card("K", "hearts"), card("K", "spades"), JOKER,
];
// `first`: the phase of the first tap on the group's cards.
const POSE_GROUPS = [
  {
    cards: [card("9", "hearts"), card("10", "hearts"), card("J", "hearts"), card("Q", "hearts"), card("K", "hearts")],
    set: false, pts: 49, first: 1, left: 8,
  },
  { cards: [card("8", "spades"), card("8", "diamonds"), card("8", "clubs")], set: true, pts: 24, first: 9, left: 140 },
];
const POSE_LAST = card("K", "spades"); // the card discarded to end the turn
const POSE_TOP = card("7", "clubs"); // already on the discard pile
const POSE_TIMES = [600, 300, 300, 300, 300, 300, 400, 800, 900, 450, 300, 300, 400, 800, 1000, 450, 800, 2600];
//                  aim  tap ×5                      grab drag drop tap ×3        grab drag drop K♠: grab drag drop
const P_GRAB = 15; // the K♠ is taken,
const P_DRAG = 16; // dragged onto the discard pile,
const P_END = 17; // and dropped: the turn is over, the groups are posted
const POSE_THRESHOLD = 71; // the default threshold of a new game
const POSE_NEXT = 74; // the threshold after this pose of 73
const POSE_HAND_Y = 160;
const POSE_TABLE_W = 228; // the table; the discard pile stands on its right
const POSE_DROP = { x: POSE_TABLE_W / 2 - CARD_W / 2, y: 62 }; // the stack, over the bar
const POSE_PILE = { x: 248, y: 21 }; // the top of the discard pile

// Where a group stands in a phase: how many of its cards are picked, and
// whether they are in the hand, held, dragged or posed.
function poseStage(group, phase) {
  const n = group.cards.length;
  const picked = Math.max(0, Math.min(n, phase - group.first + 1));
  const at = phase - group.first - n;
  return { picked, stage: at < 0 ? "hand" : at === 0 ? "grab" : at === 1 ? "drag" : "posed" };
}

// A group being composed, drawn like the pending groups of TableCombinations;
// once the turn is over, like a group of the table (its owner, no ×).
function MiniPending({ left, set, pts, cards, posted }) {
  const me = t("common.me");
  return (
    <motion.div
      className={`absolute overflow-hidden rounded-lg border transition-colors duration-500 ${
        posted ? "border-white/10 bg-slate-900/80" : "border-amber-400/30 bg-amber-950/55 shadow-[0_0_12px_rgba(251,191,36,0.12)]"
      }`}
      style={{ left, top: 20 }}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 20, delay: 0.1 }}
    >
      <div className={`flex items-center gap-1 border-b px-1.5 pb-0.5 pt-1 ${posted ? "border-white/[0.06]" : "border-amber-400/10"}`}>
        {posted && (
          <>
            <span
              className="flex h-2.5 w-2.5 items-center justify-center rounded-full text-[5px] font-bold"
              style={{ background: PLAYER_COLORS[0].bg, color: PLAYER_COLORS[0].hex, boxShadow: `0 0 0 1px ${PLAYER_COLORS[0].border}` }}
            >
              {initial(me)}
            </span>
            <span className="text-[6.5px] font-semibold leading-none" style={{ color: PLAYER_COLORS[0].hex }}>{me}</span>
          </>
        )}
        <span
          lang="fr"
          className={`rounded px-0.5 text-[5.5px] font-bold uppercase ${set ? "bg-violet-500/20 text-violet-300" : "bg-sky-500/20 text-sky-300"}`}
        >
          {set ? t("table.set") : t("table.seq")}
        </span>
        <span className="ms-auto ps-1 text-[6.5px] font-bold tabular-nums text-amber-300">{pts}</span>
        {!posted && <X className="h-2 w-2 text-rose-400" />}
      </div>
      <div className="flex gap-0.5 p-1">
        {cards.map((c) => <MiniCard key={c.id} c={c} />)}
      </div>
    </motion.div>
  );
}

export function PoseVisual() {
  useLang();
  const [phase, loop] = usePhase(POSE_TIMES, POSE_TIMES.length - 1);
  const states = POSE_GROUPS.map((g) => poseStage(g, phase));
  const groupOf = (id) => POSE_GROUPS.findIndex((g) => g.cards.some((c) => c.id === id));
  const ended = phase >= P_END;
  // The hand closes up once a group is posed; a held card keeps its slot.
  const row = POSE_HAND.filter((c) => {
    if (c.id === POSE_LAST.id) return !ended;
    const g = groupOf(c.id);
    return g < 0 || states[g].stage !== "posed";
  });
  const handX = (BOARD_W - (CARD_W + (row.length - 1) * SLICE)) / 2;
  const slot = (id) => ({ x: handX + row.findIndex((c) => c.id === id) * SLICE, y: POSE_HAND_Y });
  // The stack forms where its last card was, lifted.
  const grabAt = (g) => {
    const cards = POSE_GROUPS[g].cards;
    const s = slot(cards[cards.length - 1].id);
    return { x: s.x, y: s.y - 30 };
  };
  const lastLifted = { x: slot(POSE_LAST.id).x, y: POSE_HAND_Y - 14 };
  const posed = POSE_GROUPS.filter((g, i) => states[i].stage === "posed");
  const points = posed.reduce((sum, g) => sum + g.pts, 0);
  const dragging = states.some((st) => st.stage === "drag");

  // The finger: every phase sets every value.
  const tapAt = (c) => ({ x: slot(c.id).x + 7 - TIP.x, y: POSE_HAND_Y + 16 - TIP.y });
  const onStack = (at) => ({ x: at.x + CARD_W / 2 - TIP.x, y: at.y + 24 - TIP.y });
  const tap = (to, move) => ({
    ...to,
    opacity: 1,
    scale: [1, 1, 0.8, 1],
    transition: { x: { duration: move }, y: { duration: move }, scale: { duration: 0.28 + move, times: [0, 0.45, 0.75, 1] } },
  });
  const tapping = (g) => phase >= POSE_GROUPS[g].first && phase < POSE_GROUPS[g].first + POSE_GROUPS[g].cards.length;
  let finger;
  if (phase === 0) {
    const to = tapAt(POSE_GROUPS[0].cards[0]);
    finger = { x: [BOARD_W / 2, to.x], y: [120, to.y], opacity: [0, 1], scale: 1, transition: { duration: 0.5, ease: "easeOut" } };
  } else if (tapping(0) || tapping(1)) {
    const g = tapping(0) ? 0 : 1;
    const k = phase - POSE_GROUPS[g].first;
    finger = tap(tapAt(POSE_GROUPS[g].cards[k]), g === 1 && k === 0 ? 0.3 : 0.12);
  } else if (states.some((st) => st.stage === "grab")) {
    finger = { ...onStack(grabAt(states[0].stage === "grab" ? 0 : 1)), opacity: 1, scale: 0.85, transition: { duration: 0.25 } };
  } else if (dragging) {
    finger = { ...onStack(POSE_DROP), opacity: 1, scale: 0.85, transition: DRAG };
  } else if (phase === P_GRAB) {
    const reach = { x: { duration: 0.3 }, y: { duration: 0.3 }, scale: { duration: 0.35 } };
    finger = { ...onStack(lastLifted), opacity: 1, scale: 0.85, transition: reach };
  } else if (phase === P_DRAG) {
    finger = { ...onStack(POSE_PILE), opacity: 1, scale: 0.85, transition: DRAG };
  } else if (ended) {
    const at = onStack(POSE_PILE);
    finger = { x: at.x, y: at.y + 16, opacity: 0, scale: 1, transition: { duration: 0.4, delay: 0.25 } };
  } else {
    finger = { ...onStack(POSE_DROP), opacity: 1, scale: 1, transition: { duration: 0.2 } }; // lets go
  }

  return (
    <motion.div key={loop} dir="ltr" className="relative h-[206px] w-[300px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* The threshold badge of the top bar: the points of the groups being composed while there are some,
          then the new threshold once they are posted */}
      <div className="absolute left-0 top-0 flex items-center justify-center gap-1.5" style={{ width: POSE_TABLE_W }}>
        <span className="rami-glass flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[7px] text-amber-300">
          {points === 0 || ended ? (
            <motion.span key={ended ? "next" : "start"} initial={ended ? { scale: 1.4 } : false} animate={{ scale: 1 }}>
              {t("top.threshold", { n: ended ? POSE_NEXT : POSE_THRESHOLD })}
            </motion.span>
          ) : (
            <>
              <motion.b
                key={points}
                initial={{ scale: 1.5 }}
                animate={{ scale: 1 }}
                className={`tabular-nums ${points >= POSE_THRESHOLD ? "text-emerald-300" : "text-rose-300"}`}
              >
                {points}
              </motion.b>
              <span className="text-white/30">/</span>
              <b>{POSE_THRESHOLD}</b>
            </>
          )}
        </span>
        {points > 0 && (
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[6.5px] font-semibold text-emerald-300">
            ✓ {t("tuto.nemna3.pure")}
          </motion.span>
        )}
      </div>

      {/* The table: the groups, then the bar for a new group */}
      {posed.map((g) => <MiniPending key={g.left} {...g} posted={ended} />)}
      <div
        className={`absolute flex h-[20px] items-center justify-center rounded-lg border border-dashed transition-all duration-200 ${
          dragging ? "border-amber-300/70 bg-amber-400/[0.07] shadow-[0_0_10px_rgba(251,191,36,0.18)] delay-300" : "border-white/25"
        }`}
        style={{ left: 8, top: 76, width: POSE_TABLE_W - 8 }}
      >
        <span className="text-[6px] font-bold uppercase tracking-[0.14em] text-white/60">{t("table.drop3")}</span>
      </div>

      {/* The discard pile (PiocheArea), lit up while a card is dragged over it */}
      <span className="absolute top-[4px] w-[52px] text-center text-[6px] font-semibold uppercase text-amber-300/80" style={{ left: 238 }}>
        {t("pioche.discardLabel")}
      </span>
      <div
        className={`absolute top-[14px] h-[62px] w-[42px] rounded-lg border-2 transition-all duration-300 ${
          phase === P_DRAG ? "border-amber-400/80 bg-amber-500/[0.18] shadow-[0_0_14px_rgba(251,191,36,0.32)] delay-300" : "border-amber-400/30 bg-amber-500/[0.04]"
        }`}
        style={{ left: 243 }}
      />
      <div className="absolute opacity-90" style={{ left: POSE_PILE.x, top: POSE_PILE.y, zIndex: 29 }}>
        <PlayingCard card={POSE_TOP} size="xs" />
      </div>

      {/* The hand (PlayerHand): a picked card rises; a held group moves as one stack */}
      {POSE_HAND.map((c, i) => {
        const g = groupOf(c.id);
        const st = g >= 0 ? states[g] : null;
        const n = g >= 0 ? POSE_GROUPS[g].cards.length : 0;
        const k = g >= 0 ? POSE_GROUPS[g].cards.findIndex((x) => x.id === c.id) : -1;
        const inHand = !st || st.stage === "hand";
        const discarding = c.id === POSE_LAST.id && phase >= P_GRAB;
        let place;
        let z = i;
        let transition = SPRING;
        if (discarding) {
          // Taken from the hand and dragged onto the discard pile, as in the turn.
          const held = phase < P_END;
          place = { ...(phase === P_GRAB ? lastLifted : POSE_PILE), opacity: 1, scale: held ? 1.1 : 1, rotate: held ? -4 : 0 };
          z = held ? 45 : 30;
          transition = phase === P_DRAG ? DRAG : SPRING;
        } else if (inHand) {
          place = { ...slot(c.id), opacity: 1, scale: 1, rotate: 0 };
        } else {
          // As in the DragOverlay: the held card in front, two others behind it.
          const behind = n - 1 - k;
          const off = Math.min(behind, 2);
          const base = st.stage === "grab" ? grabAt(g) : POSE_DROP;
          const gone = st.stage === "posed";
          place = {
            x: base.x + off * 6,
            y: base.y - off * 3,
            opacity: gone || behind > 2 ? 0 : behind > 0 ? 0.6 : 1,
            scale: gone ? 0.9 : 1,
            rotate: 0,
          };
          z = 40 - behind;
          transition = st.stage === "drag" ? DRAG : { duration: 0.25 };
        }
        const holdsGroup = st && (st.stage === "grab" || st.stage === "drag") && k === n - 1;
        return (
          <motion.div key={c.id} className="absolute left-0 top-0" style={{ zIndex: z }} initial={false} animate={place} transition={transition}>
            <PlayingCard card={c} size="xs" selected={inHand && k >= 0 && k < st.picked} />
            {holdsGroup && (
              <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[7px] font-bold text-slate-950">
                {n}
              </span>
            )}
          </motion.div>
        );
      })}

      {/* The finger, pressing from its tip */}
      <motion.div
        className="absolute left-0 top-0 z-50"
        style={{ originX: TIP.x / 28, originY: TIP.y / 28 }}
        initial={false}
        animate={finger}
      >
        <Finger />
      </motion.div>
    </motion.div>
  );
}

// ── 7. After the first pose: add a card to someone else's combination ──────
const T_ROW = [card("4", "spades"), card("5", "spades"), card("6", "spades")];
const T_TIMES = [1400, 1500, 2100];
const T_STEP = 48; // card + gap
const T_CARDS_Y = 30;

export function TableVisual() {
  const [phase, loop] = usePhase(T_TIMES, 2);
  const added = phase >= 1;
  const slotX = 12 + 3 * T_STEP;
  return (
    <motion.div key={loop} dir="ltr" className="relative h-[176px] w-[224px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* A combination of Leïla's on the table, drawn like TableCombinations */}
      <div className="absolute inset-x-0 top-0 h-[104px] overflow-hidden rounded-xl border border-white/10 bg-slate-950/40">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent" />
        <div className="flex h-7 items-center gap-1.5 px-3 pt-1 text-[11px]">
          <span className="h-2 w-2 rounded-full bg-pink-400" />
          <span className="font-semibold text-white">{t("tuto.who.leila")}</span>
          <span lang="fr" className="rounded bg-white/10 px-1 text-[9px] font-bold uppercase text-emerald-100/70">{t("table.seq")}</span>
          <span className="ms-auto tabular-nums text-amber-200/80">{t("common.pts", { n: added ? 22 : 15 })}</span>
        </div>
        {T_ROW.map((c, i) => (
          <div key={c.id} className="absolute" style={{ left: 12 + i * T_STEP, top: T_CARDS_Y }}>
            <PlayingCard card={c} size="sm" />
          </div>
        ))}
        <div className="absolute h-16 w-11 rounded-lg border border-dashed border-emerald-300/30" style={{ left: slotX, top: T_CARDS_Y }} />
      </div>
      <span
        className={`absolute top-[134px] w-[140px] text-end text-[10px] text-emerald-100/60 transition-opacity duration-300 ${added ? "opacity-0" : ""}`}
        style={{ left: slotX - 152 }}
      >
        {t("tuto.hand")}
      </span>
      {/* +1: the panel's border, so the card lands exactly on the slot */}
      <motion.div
        className="absolute left-0 top-0"
        initial={false}
        animate={{ x: slotX + 1, y: added ? T_CARDS_Y + 1 : 112 }}
        transition={SPRING}
      >
        <div className={`rounded-lg ring-2 ${added ? "ring-emerald-400" : "ring-amber-300"}`}>
          <PlayingCard card={card("7", "spades")} size="sm" />
        </div>
        {added && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 14, delay: 0.35 }}
            className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white"
          >
            <Plus className="h-3 w-3" />
          </motion.span>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── 8. End of the round: who scores what ────────────────────────────────────
const SCORE_ROWS = [
  { id: "me", name: "common.me", dot: "bg-amber-400", points: "0" },
  { id: "leila", name: "tuto.who.leila", dot: "bg-pink-400", points: "+64", hand: [card("K", "clubs"), card("4", "diamonds"), JOKER], sum: "10+4+50" },
  { id: "sami", name: "tuto.who.sami", dot: "bg-sky-400", points: "+100" },
];

export function ScoreVisual() {
  const { isRtl } = useLang();
  return (
    <div className="flex w-[272px] flex-col gap-1.5">
      {SCORE_ROWS.map((r, i) => (
        <motion.div
          key={r.id}
          initial={{ opacity: 0, x: isRtl ? 24 : -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 + i * 0.3 }}
          className="flex h-11 items-center gap-2 rounded-lg border border-white/5 bg-slate-950/45 px-2.5"
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${r.dot}`} />
          <span className="w-12 shrink-0 truncate text-xs font-semibold text-white">{t(r.name)}</span>
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {r.id === "me" && (
              <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold text-amber-200">{t("summary.nefrechBadge")}</span>
            )}
            {r.hand && (
              <>
                <span dir="ltr" className="flex gap-1">
                  {r.hand.map((c) => <MiniCard key={c.id} c={c} />)}
                </span>
                <span dir="ltr" className="text-[10px] tabular-nums text-emerald-100/50">{r.sum}</span>
              </>
            )}
            {r.id === "sami" && (
              <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-200">{t("summary.notPosed")}</span>
            )}
          </div>
          <span
            dir="ltr"
            className={`flex shrink-0 items-center gap-1 font-display text-base tabular-nums ${r.id === "me" ? "text-amber-200" : "text-rose-300"}`}
          >
            {r.id === "me" && <Trophy className="h-3.5 w-3.5" />}
            {r.points}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

// ── 9. Ready ────────────────────────────────────────────────────────────────
const READY_FAN = [card("A", "spades"), JOKER, card("K", "hearts")];
const SPARKS = [
  { left: "20%", top: "20%", size: 14, delay: 0 },
  { left: "76%", top: "16%", size: 11, delay: 0.6 },
  { left: "82%", top: "60%", size: 15, delay: 1.1 },
  { left: "14%", top: "64%", size: 10, delay: 0.3 },
];

export function ReadyVisual() {
  const reduce = useReducedMotion();
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <div dir="ltr" className="-mt-4 flex items-end">
        {READY_FAN.map((c, i) => (
          <motion.div
            key={c.id}
            className="-mx-1.5 origin-bottom"
            initial={{ opacity: 0, y: 30, rotate: 0 }}
            animate={{ opacity: 1, y: i === 1 ? -6 : 4, rotate: (i - 1) * 14 }}
            transition={{ ...SPRING, delay: i * 0.08 }}
          >
            <PlayingCard card={c} size="sm" />
          </motion.div>
        ))}
      </div>
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.35 }}
        className="absolute bottom-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/30 ring-4 ring-emerald-950"
      >
        <GraduationCap className="h-6 w-6" />
      </motion.span>
      {SPARKS.map((s) => (
        <motion.span
          key={s.left}
          className="absolute text-amber-300"
          style={{ left: s.left, top: s.top, fontSize: s.size }}
          animate={reduce ? { opacity: 0.8 } : { opacity: [0, 1, 0], scale: [0.6, 1.1, 0.6] }}
          transition={reduce ? undefined : { duration: 1.8, repeat: Infinity, delay: s.delay }}
        >
          ✦
        </motion.span>
      ))}
    </div>
  );
}
